import { useSeoMeta, useHead } from '@unhead/react';
import { Hero } from '@/components/Hero';
import { InfoCard } from '@/components/InfoCard';
import { ReviewCard } from '@/components/ReviewCard';
import { SiteLinks } from '@/components/SiteLinks';
import {Shield, Truck, Package, Clock, Phone, Star, CheckCircle2, ClipboardList, Map, ThumbsUp, Briefcase, Leaf, Mail} from 'lucide-react';
import { Link } from 'react-router-dom';
import { SiteShell } from '@/components/SiteShell';
import { normalizeCanonicalUrl } from '@/lib/seo';
import teamCarrying from '../../images/VIP House Movers Team Carrying Items.jpg';
import teamLoading from '../../images/VIP House Movers Team Loading Removal Van.jpg';
import teamPacking from '../../images/VIP House Movers Team Packing.jpg';
import teamCustomer from '../../images/VIP House Movers Team with happy customer.jpg';
import aboutImage from '../../images/VIP House Movers Removal Team working.jpg';
import gardenClearance from '../../images/vip-house-movers-garden-furniture-clearance.jpeg';
import howItWorksBg from '../../images/VIP House Movers Removal Boxes.jpg';
import ecoPacking from '../../images/VIP House Movers Team Packing.jpg';
import ogImage from '../../images/VIP House Movers Removal Team with Fleet.png';

const reviews = [
  {
    name: "Hannah Schuster",
    text: "My move all went smoothly thanks to the team; they worked hard all day. Competitive price and a really helpful company."
  },
  {
    name: "Diane Wood",
    text: "Amazing service from start to finish , great price and lovely guys on the day , definitely recommend"
  },
  {
    name: "Andrew Beckett",
    text: "It's was the first time we ever moved, I read all about vip house movers, the reviews were very good, so we gave them a call. Best decision i ever did. Very professional, stress free, friendly, and easy to talk to. Can't thank them enough 👌"
  },
  {
    name: "Angela Beckett",
    text: "Absolutely brilliant so caring and professional would recommend 💯\nBest guy's out, could have a laugh while they carefully emptied the house and moved us to our new home .\nCan't thank them enough. Brilliant 👏"
  },
  {
    name: "Clare Harris",
    text: "They moved a 3 piece suite I'd found on Marketplace for me. Really nice polite and professional guys. Really happy with them"
  },
  {
    name: "Grace Phillips",
    text: "Staff was really friendly, very polite, extremely helpful too highly recommend, VIP House Movers helped me pick up some sofas and Val was great. He went to collect them and dropped them straight off for me. I’m a mom of 2 so it really helped a lot. Him and another lad sorry I forgot his name did great and made it all easy for me. Really appreciate the help."
  },
  {
    name: "Melanie Guest",
    text: "VIP House Movers was brilliant! Elvis made my move simple and completely stress-free. We were a little worried because we have a lot of artwork and a piano, but he took charge and everything went perfectly. Thanks again to VIP House Movers for such an amazing experience! VIP House Movers was brilliant! Elvis made my move simple and completely stress-free. We were a little worried because we have a lot of artwork and a piano, but he took charge and everything went perfectly. Thanks again to VIP House Movers for such an amazing experience!"
  },
  {
    name: "Rebecca Turner",
    text: "What a stress free experience I received!\n\nDue to being let down 24hours before complishion day! VIP house mover stepped and saves the day at such sort notice. They were truly amazing. From start to finish they dismantled and assembled our beds and wardrobes. But every single box and item in the correct rooms!. I will definitely be using this company again and I would recommend them to all my friends and family"
  }
];

const faqs = [
  {
    q: "Are you fully insured?",
    a: "Yes, we hold comprehensive goods in transit and public liability insurance. Your belongings are fully protected from the moment they are in our care until they are safely placed in your new home."
  },
  {
    q: "Do you supply packing materials or packing services?",
    a: "We offer both! We can provide high-quality boxes, bubble wrap, and tape if you wish to pack yourself, or our expert team can provide a full or partial packing service to save you time and stress."
  },
  {
    q: "Can you dismantle and reassemble my furniture?",
    a: "Absolutely. Our professional moving team comes fully equipped with the right tools to safely dismantle large items like beds and wardrobes, and carefully reassemble them in your new property."
  },
  {
    q: "How far in advance should I book my move?",
    a: "We recommend booking 2-4 weeks in advance to secure your preferred moving date, especially for Fridays and end-of-month moves. However, we always aim to accommodate last-minute or short-notice moves when we have availability."
  },
  {
    q: "Are your moving services eco-friendly?",
    a: "Yes! As an eco-friendly company, we are committed to reducing our environmental impact. We use sustainable, recyclable packing materials, optimize our transport routes for fuel efficiency, and ensure responsible disposal and recycling of unwanted items during whole-house or office clearances."
  }
];

function Home() {
  useSeoMeta({
    title: 'House Removal Company Wolverhampton | Home Removals & Moving Services | VIP House Movers',
    description: 'Best house removal company in Wolverhampton. Professional home removals, house removals, man and van, packing and storage services. Fully insured, 5★ rated. Free quote today.',
    ogTitle: 'House Removal Company in Wolverhampton | VIP House Movers',
    ogDescription: 'Premium house removal company in Wolverhampton. Eco-friendly, fully insured home & office removals across West Midlands. Get your free quote.',
    ogImage: ogImage,
    ogUrl: 'https://www.viphousemovers.com/',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: 'House Removal Company Wolverhampton | VIP House Movers',
    twitterDescription: 'Premium house removal company in Wolverhampton - professional, eco-friendly removals across the West Midlands.',
    twitterImage: ogImage,
  });
  const canonicalHref = normalizeCanonicalUrl('https://www.viphousemovers.com/');

  useHead({
    link: [{ rel: 'canonical', href: canonicalHref }],
    htmlAttrs: { lang: 'en-GB' },
    meta: [
      {
        name: 'keywords',
        content: 'house removals Wolverhampton, Wolverhampton home removals, Wolverhampton moving company, local removals West Midlands, man and van Wolverhampton, packing services Wolverhampton, storage solutions Wolverhampton, home removal specialists, insured removals Wolverhampton, trusted movers',
      },
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          '@id': 'https://www.viphousemovers.com',
          name: 'VIP House Movers',
          description: 'Premium house removal company offering professional home removals, office moves, and packing services in Wolverhampton and across the UK.',
          url: 'https://www.viphousemovers.com',
          telephone: '0121 752 7107',
          email: 'info@viphousemovers.com',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Vulcan Rd',
            addressLocality: 'Wolverhampton',
            addressRegion: 'West Midlands',
            postalCode: 'WV14 7LF',
            addressCountry: 'GB'
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: '52.562214533102754',
            longitude: '-2.063764422986951'
          },
          image: 'https://www.viphousemovers.com/images/VIP%20House%20Movers%20Removal%20Team%20with%20Fleet.png',
          priceRange: '£',
          sameAs: [
            'https://share.google/DTKsiSUIzQlBgy7Mi',
            'https://www.yell.com/biz/vip-house-movers-ltd-bilston-11029316/',
            'https://www.getamover.co.uk/movers/nK4Lx/vip-house-movers/',
            'https://www.comparemymove.com/directory/vip-house-movers-1'
          ],
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '5',
            reviewCount: '8'
          },
          review: [
            {
              '@type': 'Review',
              author: {
                '@type': 'Person',
                name: 'Hannah Schuster'
              },
              reviewRating: {
                '@type': 'Rating',
                ratingValue: '5'
              },
              reviewBody: 'My move all went smoothly thanks to the team; they worked hard all day. Competitive price and a really helpful company.'
            },
            {
              '@type': 'Review',
              author: {
                '@type': 'Person',
                name: 'Diane Wood'
              },
              reviewRating: {
                '@type': 'Rating',
                ratingValue: '5'
              },
              reviewBody: 'Amazing service from start to finish, great price and lovely guys on the day, definitely recommend'
            }
          ],
          areaServed: [
            {
              '@type': 'City',
              name: 'Wolverhampton',
              containedIn: {
                '@type': 'AdministrativeArea',
                name: 'West Midlands'
              }
            },
            {
              '@type': 'Country',
              name: 'United Kingdom'
            }
          ],
          serviceType: ['Residential Moving', 'Commercial Moving', 'Packing Services', 'Storage Services']
        }),
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          url: 'https://www.viphousemovers.com',
          name: 'VIP House Movers',
          alternateName: 'VIP House Movers Wolverhampton',
          siteNavigationElement: [
            { '@type': 'SiteNavigationElement', name: 'Home', url: 'https://www.viphousemovers.com/' },
            { '@type': 'SiteNavigationElement', name: 'Services', url: 'https://www.viphousemovers.com/services' },
            { '@type': 'SiteNavigationElement', name: 'Get a Quote', url: 'https://www.viphousemovers.com/quote' },
            { '@type': 'SiteNavigationElement', name: 'Areas We Cover', url: 'https://www.viphousemovers.com/areas-we-cover' },
            { '@type': 'SiteNavigationElement', name: 'Pricing', url: 'https://www.viphousemovers.com/pricing' },
            { '@type': 'SiteNavigationElement', name: 'Reviews', url: 'https://www.viphousemovers.com/reviews' },
            { '@type': 'SiteNavigationElement', name: 'About Us', url: 'https://www.viphousemovers.com/about' },
            { '@type': 'SiteNavigationElement', name: 'Blog', url: 'https://www.viphousemovers.com/blog' },
            { '@type': 'SiteNavigationElement', name: 'FAQ', url: 'https://www.viphousemovers.com/faq' },
            { '@type': 'SiteNavigationElement', name: 'Contact', url: 'https://www.viphousemovers.com/contact' }
          ]
        })
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://www.viphousemovers.com'
            }
          ]
        }),
      }
    ]
  });
  return (
    <SiteShell activePath="/" fixedNav>
      {/* Hero Section */}
      <Hero />

      {/* Trust Badges */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 text-center text-gray-500 font-semibold uppercase tracking-wider text-sm">
            <div className="flex flex-col items-center gap-3 hover:text-gray-900 transition-colors">
              <Shield className="w-8 h-8 text-[#0F4A4E]" />
              <span>Full Liability Insured</span>
            </div>
            <div className="flex flex-col items-center gap-3 hover:text-gray-900 transition-colors">
              <Clock className="w-8 h-8 text-[#0F4A4E]" />
              <span>Punctual & Swift</span>
            </div>
            <div className="flex flex-col items-center gap-3 hover:text-gray-900 transition-colors">
              <Star className="w-8 h-8 text-[#0F4A4E]" />
              <span>5-Star Service</span>
            </div>
            <div className="flex flex-col items-center gap-3 hover:text-gray-900 transition-colors">
              <Package className="w-8 h-8 text-[#0F4A4E]" />
              <span>Expert Handling</span>
            </div>
            <div className="flex flex-col items-center gap-3 hover:text-gray-900 transition-colors col-span-2 md:col-span-1">
              <Leaf className="w-8 h-8 text-green-600" />
              <span>Eco-Friendly</span>
            </div>
          </div>
        </div>
      </div>

      {/* Wolverhampton Focus Section */}
      <section id="wolverhampton" className="py-24 bg-[#F7FAFB] relative overflow-hidden border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-flex items-center rounded-full bg-[#E8F3F5] px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-[#0F4A4E]">
                Wolverhampton House Removals</span>
              <h2 className="mt-6 text-4xl font-bold tracking-tight text-gray-900">Fast, local house removals across Wolverhampton</h2>
              <p className="mt-6 text-lg leading-relaxed text-gray-600">
                VIP House Movers is a Wolverhampton-based removals company specialising in home removals, flat moves, and office relocations throughout the city. We serve all neighbourhoods from Bilston, Wednesfield and Willenhall to Tettenhall, Bushbury, and Sedgley.
              </p>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {[
                  'Local Wolverhampton moving experts with city knowledge',
                  'Personalised quotes for house removals, offices, and student moves',
                  'Same-day moves, late-night collections and flexible booking',
                  'Fully insured moves with specialist packing and assembly',
                ].map((item) => (
                  <div key={item} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                    <p className="text-gray-700 font-medium">{item}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link to="/quote" className="inline-flex items-center justify-center rounded-xl bg-[#0F4A4E] px-8 py-4 text-white font-semibold shadow-lg shadow-[#0F4A4E]/20 hover:bg-[#13595E] transition-colors">
                  Get a Free Wolverhampton Quote
                </Link>
                <Link to="/areas-we-cover/wolverhampton" className="inline-flex items-center justify-center rounded-xl border border-[#0F4A4E] px-8 py-4 text-[#0F4A4E] font-semibold hover:bg-[#0F4A4E]/5 transition-colors">
                  Wolverhampton Service Area
                </Link>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-[2rem] shadow-2xl">
              <img
                src={aboutImage}
                alt="VIP House Movers team completing a Wolverhampton house move with professional care"
                width={1200}
                height={900}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-24 bg-white relative overflow-hidden border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#E7F2F2] rounded-3xl transform rotate-3"></div>
              <img 
                src={aboutImage} 
                alt="Professional moving team" 
                className="relative rounded-2xl shadow-2xl object-cover h-[500px] w-full"
              />
              <div className="absolute -bottom-8 -right-8 bg-gray-900 text-white p-6 rounded-2xl shadow-xl hidden md:block">
                <div className="text-4xl font-bold text-[#0F4A4E] mb-1">100+</div>
                <div className="font-medium text-gray-300">Successful Moves<br/>Every Month</div>
              </div>
            </div>
            <div>
              <h2 className="text-[#0F4A4E] font-semibold tracking-wider uppercase mb-3">Why Choose Us</h2>
              <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">Your Trusted Removal Experts</h3>
              <p className="text-lg text-gray-600 leading-relaxed font-light mb-8">
                At VIP House Movers, we understand that moving can be one of life's most stressful events. That's why our dedicated team goes above and beyond to provide a seamless, premium moving experience. We treat your belongings with the utmost respect and care, ensuring a smooth transition to your new property.
              </p>
              <ul className="space-y-4">
                {[
                  'Fully insured for complete peace of mind',
                  'Professional, uniformed, and highly trained staff',
                  'Modern fleet of specialized moving vehicles',
                  'Transparent pricing with no hidden fees',
                  'Furniture dismantling and reassembly included',
                  'Committed to eco-friendly practices and sustainable packing materials'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-4">
                    <div className="flex-shrink-0 bg-[#E7F2F2] p-2 rounded-full">
                      <CheckCircle2 className="w-5 h-5 text-[#0F4A4E]" />
                    </div>
                    <span className="text-gray-700 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-900 text-white relative overflow-hidden">
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#0F4A4E]/50 to-transparent"></div>
        <img
          src={howItWorksBg}
          alt="VIP House Movers professional removal team loading furniture into a moving van in Wolverhampton"
          width={1920}
          height={1280}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover opacity-10"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <h2 className="text-[#0F4A4E] font-semibold tracking-wider uppercase mb-3">Our Process</h2>
            <h3 className="text-4xl font-bold mb-4 tracking-tight">How It Works</h3>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg font-light">A simple, completely stress-free 4-step process to get you into your new home smoothly.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
            <div className="hidden md:block absolute top-12 left-[12%] right-[12%] h-0.5 bg-gradient-to-r from-[#0F4A4E]/20 via-[#0F4A4E] to-[#0F4A4E]/20 z-0"></div>
            
            {[
              { icon: <ClipboardList />, title: "1. Get a Quote", desc: "Contact us outlining your inventory and move dates for a free, transparent estimate." },
              { icon: <Package />, title: "2. Plan & Pack", desc: "We'll coordinate logistics, and supply materials or provide full packing services if required." },
              { icon: <Truck />, title: "3. Moving Day", desc: "Our punctual team arrives, securely loads your items, and safely transports them." },
              { icon: <ThumbsUp />, title: "4. Settle In", desc: "We unload, reassemble furniture, and place items strictly in your designated rooms." }
            ].map((step, i) => (
              <div key={i} className="relative z-10 flex flex-col items-center text-center group">
                <div className="w-24 h-24 bg-gray-800 rounded-full flex items-center justify-center mb-6 border-4 border-gray-900 shadow-[0_0_0_2px_rgba(15,74,78,0.22)] group-hover:bg-[#0F4A4E] group-hover:text-white transition-all duration-300">
                  <div className="w-10 h-10">{step.icon}</div>
                </div>
                <h4 className="text-xl font-bold mb-3">{step.title}</h4>
                <p className="text-gray-400 font-light leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Gallery Strip */}
      <section className="bg-white py-16 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { src: teamCarrying, alt: 'VIP House Movers team carefully carrying large items into a client\u2019s new UK home' },
              { src: teamLoading, alt: 'VIP House Movers team loading a large removal van for a West Midlands relocation' },
              { src: teamPacking, alt: 'VIP House Movers team packing fragile items with professional care before a house move' },
              { src: teamCustomer, alt: 'VIP House Movers professional team with satisfied customer after a successful move' },
              { src: gardenClearance, alt: 'VIP House Movers garden furniture clearance and outdoor moving service in Wolverhampton' },
            ].map((img) => (
              <div key={img.src} className="relative overflow-hidden rounded-2xl aspect-[4/3] group">
                <img
                  src={img.src}
                  alt={img.alt}
                  width={800}
                  height={600}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Site Links - Improves SEO and Sitelinks */}
      <SiteLinks />

      {/* Services Section */}
      <section id="services" className="py-24 bg-gray-50 relative overflow-hidden border-b border-gray-200">
        <div className="absolute top-0 right-0 -mr-32 -mt-32 w-96 h-96 bg-[#0F4A4E]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -ml-32 -mb-32 w-96 h-96 bg-gray-900/5 rounded-full blur-3xl"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-[#0F4A4E] font-semibold tracking-wider uppercase mb-3">What We Do</h2>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">Our VIP Services</h3>
            <p className="text-xl text-gray-600 leading-relaxed font-light">
              Tailored moving solutions designed for minimal disruption and maximum peace of mind. Let the experts handle the heavy lifting.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <InfoCard
              icon={<Truck className="w-8 h-8" />}
              title="Home Removals"
              description="Comprehensive domestic moving services. From single items to full large family house relocations, anywhere in the UK." />

            <InfoCard
              icon={<Package className="w-8 h-8" />}
              title="Packing Services"
              description="Professional packing using high-quality materials to ensure your fragile and valuable items are perfectly protected during transit." />

            <InfoCard
              icon={<Briefcase className="w-8 h-8" />}
              title="Office Clearances"
              description="Efficient commercial moves and clearances tailored to minimize downtime. We handle IT equipment, furniture, and documents securely." />

            <InfoCard
              icon={<Shield className="w-8 h-8" />}
              title="Storage Solutions"
              description="Secure, dry, and clean long or short-term storage facilities available for when you need a little extra space or time." />
          </div>
        </div>
      </section>

      {/* Service Areas Banner */}
      <section className="bg-[#0F4A4E] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4 text-white">
              <Map className="w-12 h-12" />
              <div>
                <h3 className="text-2xl font-bold">Areas We Cover</h3>
                <p className="font-medium opacity-90">Local & National Relocations</p>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-3 text-sm font-bold text-white">
              {[
                { label: 'Wolverhampton', to: '/areas-we-cover/wolverhampton' },
                { label: 'Bilston', to: '/areas-we-cover/wolverhampton' },
                { label: 'Dudley', to: '/areas-we-cover/dudley' },
                { label: 'Walsall', to: '/areas-we-cover/walsall' },
                { label: 'Birmingham', to: '/areas-we-cover/birmingham' },
                { label: 'Nationwide UK', to: '/areas-we-cover' },
              ].map((area) => (
                <Link key={area.label} to={area.to} className="px-4 py-2 bg-white/10 rounded-full whitespace-nowrap hover:bg-white/20 transition-colors border border-white/15">
                  {area.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Eco-Friendly Feature Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-green-700 font-semibold tracking-wider uppercase mb-3">Eco-Friendly Moves</h2>
              <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">Sustainable removals you can trust</h3>
              <p className="text-lg text-gray-600 leading-relaxed font-light mb-6">
                We reduce the environmental impact of every move with recyclable packing materials, optimised fuel-efficient routes across the West Midlands, and responsible disposal for house and office clearances.
              </p>
              <ul className="space-y-4">
                {[
                  'Reusable crates and recyclable boxes',
                  'Route optimisation for lower emissions',
                  'Responsible recycling of unwanted items',
                  'Paperless booking and digital inventories',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-4">
                    <div className="flex-shrink-0 bg-green-100 p-2 rounded-full">
                      <Leaf className="w-5 h-5 text-green-700" />
                    </div>
                    <span className="text-gray-700 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="order-1 lg:order-2 relative">
              <div className="absolute -inset-4 bg-green-100 rounded-3xl -rotate-3"></div>
              <img
                src={ecoPacking}
                alt="Eco-friendly packing materials and recyclable moving boxes used by VIP House Movers for sustainable UK relocations"
                width={1200}
                height={900}
                loading="lazy"
                decoding="async"
                className="relative rounded-2xl shadow-2xl object-cover h-[450px] w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-gray-50 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-[#0F4A4E] font-semibold tracking-wider uppercase mb-3">Client Stories</h2>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">What Our Customers Say</h3>
            <p className="text-xl text-gray-600 leading-relaxed font-light">
              Don't just take our word for it. Read what our satisfied clients have to say about their moving experience with VIP House Movers.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
            {reviews.map((review, index) => (
              <ReviewCard key={index} name={review.name} text={review.text} />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-[#0F4A4E] font-semibold tracking-wider uppercase mb-3">Got Questions?</h2>
            <h3 className="text-4xl font-bold text-gray-900 mb-6 tracking-tight">Frequently Asked Questions</h3>
          </div>
          
          <div className="space-y-6">
            {faqs.map((faq, i) => (
              <details key={i} className="group bg-white rounded-2xl shadow-sm border border-gray-100 hover:border-[#B7D1D2] transition-colors [&_summary::-webkit-details-marker]:hidden">
                <summary className="flex items-center justify-between cursor-pointer p-6 font-bold text-lg text-gray-900 list-none">
                  {faq.q}
                  <span className="bg-[#E7F2F2] p-2 rounded-full text-[#0F4A4E] group-open:-rotate-180 transition-transform duration-300">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </span>
                </summary>
                <div className="px-6 pb-6 text-gray-600 font-light leading-relaxed">
                  <div className="h-px w-full bg-gray-100 mb-6"></div>
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-900 text-white py-20 relative border-t-8 border-[#0F4A4E]">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-white via-gray-900 to-gray-900"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-6 tracking-tight">Ready for a VIP Move?</h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto font-light">
            Contact our dedicated team today for a free, no-obligation quote and personalized moving plan.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:01217527107" className="inline-flex justify-center items-center gap-3 bg-[#0F4A4E] text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#13595E] hover:scale-105 transition-all duration-300 shadow-lg shadow-[#0F4A4E]/20">
              <Phone className="w-5 h-5" />
              0121 752 7107
            </a>
            <a href="mailto:info@viphousemovers.com" className="inline-flex justify-center items-center gap-3 bg-white/10 hover:bg-white/20 text-white border border-white/20 px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 backdrop-blur-sm">
              <Mail className="w-5 h-5" />
              info@viphousemovers.com
            </a>
            <Link to="/quote" className="inline-flex justify-center items-center gap-3 bg-transparent border-2 border-[#0F4A4E] text-[#9ED0D3] hover:bg-[#0F4A4E] hover:text-white px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300">
              Get a Free Quote
            </Link>
          </div>
        </div>
      </section>

    </SiteShell>);

}

export default Home;
